import { useState, useMemo, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import SocialLinks from "../components/SocialLinks";
import {
  Search,
  MapPin,
  Phone,
  Star,
  Globe,
  Plus,
  Loader2,
  Building2,
  Tag,
  Mail,
  Users,
  DollarSign,
  Filter,
  X,
  ChevronDown,
  ChevronUp
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

// All categories in alphabetical order — grouped alphabetically by industry
const CATEGORIES = [
  { value: "Accounting Firm", label: "Accounting & Tax", group: "Professional Services" },
  { value: "Auto Dealers - New Cars", label: "Auto Dealers - New Cars", group: "Automotive" },
  { value: "Auto Dealers - Used Cars", label: "Auto Dealers - Used Cars", group: "Automotive" },
  { value: "Auto Parts Store", label: "Auto Parts & Accessories", group: "Automotive" },
  { value: "Auto Repair Shop", label: "Auto Repair Shops", group: "Automotive" },
  { value: "Bakery", label: "Bakeries", group: "Food & Dining" },
  { value: "Bar", label: "Bars & Lounges", group: "Food & Dining" },
  { value: "Car Wash", label: "Car Washes", group: "Automotive" },
  { value: "Catering", label: "Catering Services", group: "Food & Dining" },
  { value: "Chiropractor", label: "Chiropractors", group: "Healthcare" },
  { value: "Cleaning Service", label: "Cleaning Services", group: "Home Services" },
  { value: "Clothing Store", label: "Clothing Stores", group: "Retail" },
  { value: "Coffee Shop", label: "Coffee Shops", group: "Food & Dining" },
  { value: "College", label: "Colleges", group: "Education" },
  { value: "Dentist", label: "Dental Offices", group: "Healthcare" },
  { value: "Dry Cleaners", label: "Dry Cleaners", group: "Personal Services" },
  { value: "Electrician", label: "Electrical Contractors", group: "Construction & Trades" },
  { value: "Fast Food Restaurant", label: "Fast Food", group: "Food & Dining" },
  { value: "Fitness Center", label: "Fitness Centers & Gyms", group: "Personal Services" },
  { value: "Florist", label: "Florists", group: "Retail" },
  { value: "Furniture Store", label: "Furniture Stores", group: "Retail" },
  { value: "Garage Door Repair", label: "Garage Door Services", group: "Home Services" },
  { value: "General Contractor", label: "General Contractors", group: "Construction & Trades" },
  { value: "Hair Salon", label: "Hair Salons", group: "Personal Services" },
  { value: "Hardware Store", label: "Hardware Stores", group: "Retail" },
  { value: "Home Care Agency", label: "Home Care Agencies", group: "Home Care" },
  { value: "Home Health Aide Service", label: "Home Health Aides", group: "Home Care" },
  { value: "Hospice Care", label: "Hospice Care", group: "Home Care" },
  { value: "In-Home Nursing", label: "In-Home Nursing", group: "Home Care" },
  { value: "Insurance Agency", label: "Insurance Agencies", group: "Professional Services" },
  { value: "IT Services", label: "IT & Computer Services", group: "Professional Services" },
  { value: "Jewelry Store", label: "Jewelry Stores", group: "Retail" },
  { value: "K-12 School", label: "K-12 Schools", group: "Education" },
  { value: "Landscaping Company", label: "Landscaping Services", group: "Construction & Trades" },
  { value: "Law Firm", label: "Legal Services", group: "Professional Services" },
  { value: "Liquor Store", label: "Liquor Stores", group: "Retail" },
  { value: "Locksmith", label: "Locksmiths", group: "Home Services" },
  { value: "Marketing Agency", label: "Marketing & Advertising", group: "Professional Services" },
  { value: "Medical Clinic", label: "Medical Clinics", group: "Healthcare" },
  { value: "Mental Health Counselor", label: "Counseling & Therapy", group: "Mental Health" },
  { value: "Addiction Treatment Center", label: "Addiction & Recovery Centers", group: "Mental Health" },
  { value: "Behavioral Health Clinic", label: "Behavioral Health Clinics", group: "Mental Health" },
  { value: "Child Psychologist", label: "Child & Adolescent Therapy", group: "Mental Health" },
  { value: "Marriage Counselor", label: "Marriage & Family Therapy", group: "Mental Health" },
  { value: "Psychiatrist", label: "Psychiatrists", group: "Mental Health" },
  { value: "Psychology Practice", label: "Psychology Practices", group: "Mental Health" },
  { value: "Moving Company", label: "Moving & Storage", group: "Home Services" },
  { value: "Non-Medical Home Care", label: "Non-Medical Home Care", group: "Home Care" },
  { value: "Painting Contractor", label: "Painting & Decorating", group: "Construction & Trades" },
  { value: "Pediatric Home Care", label: "Pediatric Home Care", group: "Home Care" },
  { value: "Pest Control", label: "Pest Control", group: "Home Services" },
  { value: "Pet Grooming", label: "Pet Grooming", group: "Personal Services" },
  { value: "Pharmacy", label: "Pharmacies", group: "Healthcare" },
  { value: "Physical Therapist", label: "Physical Therapy", group: "Healthcare" },
  { value: "Plumber", label: "Plumbing & HVAC", group: "Construction & Trades" },
  { value: "Pool Service", label: "Pool Maintenance", group: "Home Services" },
  { value: "Preschool", label: "Preschools & Daycares", group: "Education" },
  { value: "Private School", label: "Private Schools", group: "Education" },
  { value: "Real Estate Agency", label: "Real Estate", group: "Professional Services" },
  { value: "Restaurant", label: "Restaurants", group: "Food & Dining" },
  { value: "Roofing Contractor", label: "Roofing & Siding", group: "Construction & Trades" },
  { value: "Senior Home Care", label: "Senior Care Services", group: "Home Care" },
  { value: "Day Spa", label: "Spas & Massage", group: "Personal Services" },
  { value: "Tattoo Shop", label: "Tattoo Parlors", group: "Personal Services" },
  { value: "Tire Shop", label: "Tire Dealers", group: "Automotive" },
  { value: "Trade School", label: "Trade & Vocational Schools", group: "Education" },
  { value: "University", label: "Universities", group: "Education" },
  { value: "Veterinarian", label: "Veterinary Services", group: "Healthcare" },
];

// Group categories by group, sorted alphabetically
const GROUPED_CATEGORIES = (() => {
  const groups = {};
  CATEGORIES.forEach(c => {
    if (!groups[c.group]) groups[c.group] = [];
    groups[c.group].push(c);
  });
  return Object.keys(groups).sort().map(g => ({ group: g, items: groups[g] }));
})();

const SIZE_OPTIONS = [
  { value: "all", label: "All Sizes", color: "" },
  { value: "enterprise", label: "Enterprise (1000+ reviews)", color: "bg-purple-100 text-purple-700" },
  { value: "large", label: "Large (500-999 reviews)", color: "bg-blue-100 text-blue-700" },
  { value: "medium", label: "Medium (100-499 reviews)", color: "bg-emerald-100 text-emerald-700" },
  { value: "small", label: "Small (20-99 reviews)", color: "bg-amber-100 text-amber-700" },
  { value: "micro", label: "Micro (<20 reviews)", color: "bg-slate-100 text-slate-600" },
];

const sizeColors = {
  enterprise: "bg-purple-100 text-purple-700 border-purple-200",
  large: "bg-blue-100 text-blue-700 border-blue-200",
  medium: "bg-emerald-100 text-emerald-700 border-emerald-200",
  small: "bg-amber-100 text-amber-700 border-amber-200",
  micro: "bg-slate-100 text-slate-600 border-slate-200",
  unknown: "bg-slate-50 text-slate-400 border-slate-200"
};

export default function BusinessSearchPage() {
  const [searchMode, setSearchMode] = useState("category");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [searchForm, setSearchForm] = useState({
    keyword: "",
    company_name: "",
    city: "",
    state: "CA",
    zip_code: "",
    limit: 20,
  });
  const [results, setResults] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searching, setSearching] = useState(false);
  const [importing, setImporting] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");
  const dropdownRef = useRef(null);

  // Filters
  const [sizeFilter, setSizeFilter] = useState("all");
  const [hasEmailFilter, setHasEmailFilter] = useState(false);
  const [hasPhoneFilter, setHasPhoneFilter] = useState(false);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setCategoryDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredResults = useMemo(() => {
    let filtered = results;
    if (sizeFilter !== "all") {
      filtered = filtered.filter(b => b.size_estimate?.size_category === sizeFilter);
    }
    if (hasEmailFilter) {
      filtered = filtered.filter(b => b.email);
    }
    if (hasPhoneFilter) {
      filtered = filtered.filter(b => b.phone);
    }
    return filtered;
  }, [results, sizeFilter, hasEmailFilter, hasPhoneFilter]);

  const filteredCategories = useMemo(() => {
    if (!categorySearch.trim()) return GROUPED_CATEGORIES;
    const q = categorySearch.toLowerCase();
    return GROUPED_CATEGORIES.map(g => ({
      ...g,
      items: g.items.filter(c => c.label.toLowerCase().includes(q) || c.value.toLowerCase().includes(q))
    })).filter(g => g.items.length > 0);
  }, [categorySearch]);

  const activeFilters = (sizeFilter !== "all" ? 1 : 0) + (hasEmailFilter ? 1 : 0) + (hasPhoneFilter ? 1 : 0);

  const toggleCategory = (value) => {
    setSelectedCategories(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
  };

  const handleSearch = async () => {
    if (searchMode === "category" && selectedCategories.length === 0) {
      toast.error("Please select at least one category");
      return;
    }
    if (searchMode === "keyword" && !searchForm.keyword.trim()) {
      toast.error("Please enter a keyword");
      return;
    }
    if (searchMode === "company" && !searchForm.company_name.trim()) {
      toast.error("Please enter a company name");
      return;
    }
    if (!searchForm.city || !searchForm.state) {
      toast.error("Please fill in City and State");
      return;
    }

    setSearching(true);
    setHasSearched(true);
    setSelectedIds(new Set());

    try {
      let queryTerms;
      if (searchMode === "category") {
        queryTerms = selectedCategories;
      } else if (searchMode === "keyword") {
        queryTerms = [searchForm.keyword.trim()];
      } else {
        // company name search — pass the literal name; Google Places text search handles brand lookups
        queryTerms = [searchForm.company_name.trim()];
      }
      const searchPayload = {
        categories: queryTerms,
        city: searchForm.city,
        state: searchForm.state,
        zip_code: searchForm.zip_code || null,
        limit: Math.max(1, Math.min(100, parseInt(searchForm.limit, 10) || 20)),
      };

      const response = await fetch(`${API}/business-search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(searchPayload)
      });

      if (response.ok) {
        const data = await response.json();
        setResults(data.businesses || []);
        if (data.businesses?.length === 0) {
          toast.info("No businesses found. Try a different search.");
        } else {
          toast.success(`Found ${data.businesses.length} businesses across ${data.categories_searched?.length || 1} ${data.categories_searched?.length === 1 ? "category" : "categories"}`);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Search failed");
      }
    } catch (error) {
      toast.error("Failed to search businesses");
    } finally {
      setSearching(false);
    }
  };

  const toggleSelect = (placeId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(placeId)) {
        next.delete(placeId);
      } else {
        next.add(placeId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredResults.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredResults.map(b => b.place_id)));
    }
  };

  const handleImport = async () => {
    const selected = filteredResults.filter(b => selectedIds.has(b.place_id));
    if (selected.length === 0) {
      toast.error("Please select businesses to import");
      return;
    }

    setImporting(true);
    try {
      const response = await fetch(`${API}/business-search/import`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ businesses: selected })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Imported ${data.imported} businesses as leads`);
        setSelectedIds(new Set());
      } else {
        const error = await response.json();
        toast.error(error.detail || "Import failed");
      }
    } catch (error) {
      toast.error("Failed to import businesses");
    } finally {
      setImporting(false);
    }
  };

  // Size distribution stats
  const sizeDistribution = useMemo(() => {
    const dist = { enterprise: 0, large: 0, medium: 0, small: 0, micro: 0, unknown: 0 };
    results.forEach(b => {
      const cat = b.size_estimate?.size_category || "unknown";
      dist[cat] = (dist[cat] || 0) + 1;
    });
    return dist;
  }, [results]);

  return (
    <div className="space-y-6" data-testid="business-search-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <Building2 className="w-7 h-7 text-primary" />
          Business Search
        </h1>
        <p className="text-slate-500 mt-1">
          Search and import businesses from Google Places
        </p>
      </div>

      {/* Search Form */}
      <Card>
        <CardContent className="pt-6">
          <Tabs value={searchMode} onValueChange={setSearchMode}>
            <TabsList>
              <TabsTrigger value="category" data-testid="tab-category">
                <Tag className="w-4 h-4 mr-2" />
                By Category
              </TabsTrigger>
              <TabsTrigger value="keyword" data-testid="tab-keyword">
                <Search className="w-4 h-4 mr-2" />
                By Keyword
              </TabsTrigger>
              <TabsTrigger value="company" data-testid="tab-company">
                <Building2 className="w-4 h-4 mr-2" />
                By Company Name
              </TabsTrigger>
            </TabsList>

            <TabsContent value="category" className="mt-4">
              <div>
                <Label>Business Categories *</Label>
                <div ref={dropdownRef} className="relative mt-1">
                  {/* Selected tags + trigger */}
                  <div
                    className="min-h-[42px] w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm cursor-pointer flex flex-wrap items-center gap-1.5 hover:border-slate-300 transition-colors"
                    onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
                    data-testid="category-multi-select"
                  >
                    {selectedCategories.length === 0 ? (
                      <span className="text-slate-400">Select categories...</span>
                    ) : (
                      selectedCategories.map(val => {
                        const cat = CATEGORIES.find(c => c.value === val);
                        return (
                          <Badge
                            key={val}
                            variant="secondary"
                            className="text-xs px-2 py-0.5 gap-1 flex-shrink-0"
                          >
                            {cat?.label || val}
                            <X
                              className="w-3 h-3 cursor-pointer hover:text-red-500"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleCategory(val);
                              }}
                            />
                          </Badge>
                        );
                      })
                    )}
                    <div className="ml-auto pl-2 flex-shrink-0">
                      {categoryDropdownOpen ? (
                        <ChevronUp className="w-4 h-4 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                      )}
                    </div>
                  </div>

                  {/* Dropdown panel */}
                  {categoryDropdownOpen && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-[360px] overflow-hidden" data-testid="category-dropdown">
                      <div className="p-2 border-b border-slate-100">
                        <Input
                          placeholder="Search categories..."
                          value={categorySearch}
                          onChange={(e) => setCategorySearch(e.target.value)}
                          className="h-8 text-sm"
                          data-testid="category-search-input"
                          autoFocus
                        />
                      </div>
                      <div className="overflow-y-auto max-h-[300px] p-1">
                        {filteredCategories.map(group => (
                          <div key={group.group} className="mb-1">
                            <p className="px-3 py-1.5 text-xs font-semibold text-primary uppercase tracking-wider">
                              {group.group}
                            </p>
                            {group.items.map(cat => (
                              <label
                                key={cat.value}
                                className="flex items-center gap-2.5 px-3 py-1.5 hover:bg-slate-50 rounded cursor-pointer"
                                data-testid={`category-option-${cat.value}`}
                              >
                                <Checkbox
                                  checked={selectedCategories.includes(cat.value)}
                                  onCheckedChange={() => toggleCategory(cat.value)}
                                  className="h-4 w-4"
                                />
                                <span className="text-sm text-slate-700">{cat.label}</span>
                              </label>
                            ))}
                          </div>
                        ))}
                        {filteredCategories.length === 0 && (
                          <p className="text-sm text-slate-400 text-center py-4">No matching categories</p>
                        )}
                      </div>
                      {selectedCategories.length > 0 && (
                        <div className="border-t border-slate-100 px-3 py-2 flex items-center justify-between bg-slate-50">
                          <span className="text-xs text-slate-500">{selectedCategories.length} selected</span>
                          <button
                            className="text-xs text-red-500 hover:underline"
                            onClick={() => setSelectedCategories([])}
                          >
                            Clear all
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Select one or more categories to search across multiple industries at once
                </p>
              </div>
            </TabsContent>

            <TabsContent value="keyword" className="mt-4">
              <div>
                <Label htmlFor="keyword">Search Keyword *</Label>
                <Input
                  id="keyword"
                  value={searchForm.keyword}
                  onChange={(e) => setSearchForm({ ...searchForm, keyword: e.target.value })}
                  placeholder="e.g., roofing contractor, pet grooming, yoga studio"
                  data-testid="search-keyword"
                  className="w-full"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Enter any business type, service, or keyword to search
                </p>
              </div>
            </TabsContent>

            <TabsContent value="company" className="mt-4">
              <div>
                <Label htmlFor="company_name">Company Name *</Label>
                <Input
                  id="company_name"
                  value={searchForm.company_name}
                  onChange={(e) => setSearchForm({ ...searchForm, company_name: e.target.value })}
                  placeholder="e.g., Starbucks, Acme Industries, Bay Area Chiropractic"
                  data-testid="search-company-name"
                  className="w-full"
                  onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Look up a specific business by name in the chosen city/state — finds chain locations and unique brands.
                </p>
              </div>
            </TabsContent>
          </Tabs>

          {/* Location Fields */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
            <div>
              <Label htmlFor="city">City *</Label>
              <Input
                id="city"
                value={searchForm.city}
                onChange={(e) => setSearchForm({ ...searchForm, city: e.target.value })}
                placeholder="e.g., San Francisco"
                data-testid="search-city"
              />
            </div>
            <div>
              <Label htmlFor="state">State *</Label>
              <Select
                value={searchForm.state}
                onValueChange={(value) => setSearchForm({ ...searchForm, state: value })}
              >
                <SelectTrigger data-testid="search-state">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {US_STATES.map(state => (
                    <SelectItem key={state} value={state}>{state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="zip">Zip Code</Label>
              <Input
                id="zip"
                value={searchForm.zip_code}
                onChange={(e) => setSearchForm({ ...searchForm, zip_code: e.target.value })}
                placeholder="Optional"
                data-testid="search-zip"
              />
            </div>
          </div>

          {/* Result count selector */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
            <div>
              <Label htmlFor="limit">Number of leads to pull</Label>
              <div className="flex gap-2">
                <Select
                  value={String(searchForm.limit)}
                  onValueChange={(value) => setSearchForm({ ...searchForm, limit: parseInt(value, 10) })}
                >
                  <SelectTrigger data-testid="search-limit" className="flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20">20 leads</SelectItem>
                    <SelectItem value="50">50 leads</SelectItem>
                    <SelectItem value="75">75 leads</SelectItem>
                    <SelectItem value="100">100 leads</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={searchForm.limit}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (Number.isNaN(v)) return;
                    setSearchForm({ ...searchForm, limit: Math.max(1, Math.min(100, v)) });
                  }}
                  className="w-20"
                  data-testid="search-limit-input"
                  aria-label="Custom number of leads (1-100)"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Pick a preset or type any number from 1 to 100.
              </p>
            </div>
          </div>

          <Button
            onClick={handleSearch}
            disabled={searching}
            className="w-full mt-4"
            size="lg"
            data-testid="search-btn"
          >
            {searching ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Searching...
              </>
            ) : (
              <>
                <Search className="w-5 h-5 mr-2" />
                Search Businesses
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Filters */}
      {results.length > 0 && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-slate-400" />
                  <span className="text-sm font-medium text-slate-600">Filters</span>
                  {activeFilters > 0 && (
                    <Badge className="bg-primary text-white text-xs">{activeFilters}</Badge>
                  )}
                </div>

                <Select value={sizeFilter} onValueChange={setSizeFilter}>
                  <SelectTrigger className="w-[200px] h-8 text-xs" data-testid="filter-size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SIZE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex items-center gap-2">
                          {opt.color && <span className={`w-2 h-2 rounded-full ${opt.color.split(" ")[0]}`} />}
                          <span>{opt.label}</span>
                          {opt.value !== "all" && (
                            <span className="text-slate-400 ml-1">({sizeDistribution[opt.value] || 0})</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <label className="flex items-center gap-1.5 cursor-pointer">
                  <Checkbox
                    checked={hasEmailFilter}
                    onCheckedChange={setHasEmailFilter}
                    className="h-4 w-4"
                    data-testid="filter-email"
                  />
                  <Mail className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-xs text-slate-600">Has Email</span>
                </label>

                <label className="flex items-center gap-1.5 cursor-pointer">
                  <Checkbox
                    checked={hasPhoneFilter}
                    onCheckedChange={setHasPhoneFilter}
                    className="h-4 w-4"
                    data-testid="filter-phone"
                  />
                  <Phone className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-xs text-slate-600">Has Phone</span>
                </label>

                {activeFilters > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-slate-500"
                    onClick={() => { setSizeFilter("all"); setHasEmailFilter(false); setHasPhoneFilter(false); }}
                    data-testid="clear-filters"
                  >
                    <X className="w-3 h-3 mr-1" />
                    Clear Filters
                  </Button>
                )}
              </div>

              <span className="text-sm text-slate-500">
                {filteredResults.length} of {results.length} results
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {hasSearched && (
        <div className="space-y-4">
          {filteredResults.length > 0 && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={selectedIds.size === filteredResults.length && filteredResults.length > 0}
                  onCheckedChange={toggleSelectAll}
                  data-testid="select-all-results"
                />
                <span className="text-sm text-slate-500">
                  {selectedIds.size > 0 ? `${selectedIds.size} selected` : "Select all"}
                </span>
              </div>
              {selectedIds.size > 0 && (
                <Button
                  onClick={handleImport}
                  disabled={importing}
                  data-testid="import-selected-btn"
                >
                  {importing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  Import {selectedIds.size} as Leads
                </Button>
              )}
            </div>
          )}

          {filteredResults.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">
                  {results.length > 0
                    ? "No businesses match the current filters"
                    : "No businesses found. Try different search terms."}
                </p>
              </CardContent>
            </Card>
          )}

          <div className="grid gap-3">
            {filteredResults.map((business) => (
              <Card
                key={business.place_id}
                className={`transition-all ${selectedIds.has(business.place_id) ? "ring-2 ring-primary bg-primary/5" : "hover:bg-slate-50"}`}
                data-testid={`search-result-${business.place_id}`}
              >
                <CardContent className="py-4">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={selectedIds.has(business.place_id)}
                      onCheckedChange={() => toggleSelect(business.place_id)}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-slate-800">{business.name}</h3>
                            {business.rating && (
                              <Badge variant="outline" className="text-xs gap-1">
                                <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                                {business.rating}
                                {business.review_count && (
                                  <span className="text-slate-400">({business.review_count})</span>
                                )}
                              </Badge>
                            )}
                            {business.size_estimate && business.size_estimate.size_category !== "unknown" && (
                              <Badge className={`${sizeColors[business.size_estimate.size_category]} text-xs border`}>
                                {business.size_estimate.size_label}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {business.address}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 mt-2 flex-wrap">
                        {business.phone && (
                          <span className="text-sm text-slate-600 flex items-center gap-1">
                            <Phone className="w-3 h-3 text-emerald-500" />
                            {business.phone}
                          </span>
                        )}
                        {business.email && (
                          <span className="text-sm text-blue-600 flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {business.email}
                          </span>
                        )}
                        {business.website && (
                          <a href={business.website} target="_blank" rel="noopener noreferrer" className="text-sm text-slate-500 flex items-center gap-1 hover:text-primary">
                            <Globe className="w-3 h-3" />
                            Website
                          </a>
                        )}
                        {business.category && (
                          <Badge variant="outline" className="text-xs">
                            {business.category}
                          </Badge>
                        )}
                        <SocialLinks links={business.social_links} />
                      </div>

                      {business.size_estimate && business.size_estimate.size_category !== "unknown" && (
                        <div className="flex items-center gap-4 mt-2">
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            Est. {business.size_estimate.estimated_employees} employees
                          </span>
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <DollarSign className="w-3 h-3" />
                            Est. revenue {business.size_estimate.estimated_revenue}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
